import { apiBuilder, prefixApi } from "@zodios/core";
import { z } from "zod";
import { orgSchema } from "../common/orgSchema";
import { gvProductSchema } from "../common/gvProductSchema";

const offsetTypeSchema = z.literal("percent").or(z.literal("currency"));
const offsetSchema = z.object({ amount: z.number(), type: offsetTypeSchema });
export type Offset = z.infer<typeof offsetSchema>;

const competitorRuleSchema = z.object({
  asin: z.string(),
  unit: z.string(),
  value: z.number(),
  action: z.string(),
});

const inventoryRuleSchema = z.object({
  min: z.number(),
  max: z.number(),
  status: z.string(),
});

const pricingOrgSchema = z.object({
  id: z.number(),
  org: orgSchema,
  supply_target_program: z
    .object({
      rules: z.tuple([inventoryRuleSchema, inventoryRuleSchema, inventoryRuleSchema, inventoryRuleSchema]),
    })
    .nullable(),
  days_back: z.number(),
  days_with_price: z.number(),
  sales_threshold: z.number(),
  status: z.literal("enabled").or(z.literal("disabled")),
  end_digits: z.string(),
  auto_slider: z.boolean(),
  bucket_config: z.unknown(),
  limit_config: z.unknown(),
  increment_days: z.number(),
  increment_percent: z.number(),
  weekly_cycle: z.boolean(),
  refresh_days: z.number(),
  use_incoming_inventory: z.boolean(),
  send_standard_price: z.boolean(),
  send_discounted_price: z.boolean(),
  send_list_price: z.boolean(),
  send_b2b_price: z.boolean(),
  b2b_offset_amount: z.number(),
  b2b_offset_type: offsetTypeSchema,
  list_price_offset_amount: z.number(),
  list_price_offset_type: offsetTypeSchema,
  walmart_org: z.unknown().nullable(),
});
export type PricingOrg = z.infer<typeof pricingOrgSchema>;

const updatePricingConfigurationPayloadSchema = z.object({
  end_digits: z.string(),
  weekly_cycle: z.boolean(),
  use_incoming_inventory: z.boolean(),

  send_standard_price: z.boolean(),
  send_discounted_price: z.boolean(),
  send_list_price: z.boolean(),
  send_b2b_price: z.boolean(),
  b2b_offset: offsetSchema,
  list_price_offset: offsetSchema,

  days_back: z.number(),
  sales_threshold: z.number(),
  days_with_price: z.number(),

  inventory_rules: z
    .tuple([inventoryRuleSchema, inventoryRuleSchema, inventoryRuleSchema, inventoryRuleSchema])
    .nullable(),
});

const pricingPlanSchema = z.object({
  id: z.number(),
  plan_name: z.string(),
  min_price: z.number(),
  max_price: z.number(),
  configured_slider_value: z.number(),
  current_program: z.object({ template_type: z.string(), id: z.number() }).nullable(),
  confidence: z.string(),
  category_name: z.string(),
  search_rank: z.number(),
  pricing_org: pricingOrgSchema,
  plan_type: z.string(),
  status: z.string(),
  channel: z.string(),
  market_place: z.string(),
  enroll_date: z.string().datetime(),
  best_price: z.number(),
  best_price_date: z.string().datetime().nullable(),
  order_history: z
    .record(z.string(), z.object({ bsr: z.number(), price: z.number(), qty: z.number(), profit: z.number() }))
    .nullable(),
  pricing_data: z.unknown().array().nullable(),
  slider_data: z.object({ price: z.number(), slider_pos: z.number() }).array().nullable(),
  price_elasticity: z.number().nullable(),
  curve_confidence: z.number().nullable(),
  enroll_target: z.string(),
  stock_turnover_strategy: z.boolean(),
  turnover_days: z.number(),
  sigma_multiplier: z.number(),
  sales_volume_control_slider: z.number(),
  slider_step_size: z.number(),
  status_reason: z.unknown(),
  listing_status: z.unknown(),
  last_optimized_date: z.unknown(),
  seasonality_switch: z.boolean(),
  seasonality_multiplier: z.number(),
  created_date: z.string().datetime(),
  updated_date: z.string().datetime(),
  target_bsr: z.number().nullable(),
  bsr_optimization: z.boolean(),
  internal_max_price: z.number().nullable(),
  internal_min_price: z.number().nullable(),
  price_profiling: z.boolean(),
  competitor_rules: competitorRuleSchema.array().nullable(),
  relationship_rules: z.unknown(),
  slider_change_date: z.unknown(),
  visible: z.boolean(),
  is_auto: z.boolean(),
  override_units: z.boolean(),
  target_units: z.number(),
  override_margin: z.boolean(),
  target_margin: z.number(),
  override_supply: z.boolean(),
  target_supply: z.number().nullable(),
  days_of_supply: z.number(),
  tags: z.unknown().array(),
  cron_schedule: z.string(),
  next_run: z.string().datetime(),
  latest_competitor_bsr: z.number().nullable(),
  competitor_bsr_settings: z.unknown(),
  org: z.number(),
  category: z.number(),
  relationship: z.unknown(),
  created_by: z.unknown(),
  updated_by: z.unknown(),
  b2b_offset_amount: z.number().nullable(),
  b2b_offset_type: offsetTypeSchema.nullable(),
  list_offset_amount: z.number().nullable(),
  list_offset_type: offsetTypeSchema.nullable(),
});
export type PricingPlan = z.infer<typeof pricingPlanSchema>;

const updatePricingPlanPayloadSchema = z.object({
  plan_name: z.string(),
  plan_type: z.string(),
  configured_slider_value: z.number(),
  min_price: z.number(),
  max_price: z.number(),
  target_bsr: z.number().nullable(),
  competitor_rules: competitorRuleSchema.array().nullable(),
  override_margin: z.boolean().optional(),
  target_margin: z.number().optional().nullable(),
  override_units: z.boolean().optional(),
  target_units: z.number().nullable().nullable(),
  override_supply: z.boolean().optional(),
  target_supply: z.number().optional().nullable(),

  b2b_offset_amount: z.number().nullable(),
  b2b_offset_type: offsetTypeSchema.nullable(),
  list_offset_amount: z.number().nullable(),
  list_offset_type: offsetTypeSchema.nullable(),
});

export const pricingApiDefinitions = prefixApi(
  "/pricing",
  apiBuilder({
    alias: "updatePricingPlanStockRequired",
    method: "post",
    path: "/pricing_plan_products/update_stock_required",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({
          plan_id: z.number(),
          product_ids: z.array(z.number()),
          stock_required: z.boolean(),
        }),
      },
    ],
    status: 204,
    response: z.void(),
  })
    .addEndpoint({
      alias: "updatePricingPlanProductsOffset",
      method: "post",
      path: "/pricing_plan_products/update_offset",
      parameters: [
        {
          name: "body",
          type: "Body",
          schema: z.object({
            plan_id: z.number(),
            product_ids: z.array(z.number()),
            offset: offsetSchema,
          }),
        },
      ],
      status: 204,
      response: z.void(),
    })
    .addEndpoint({
      alias: "getPricingPlan",
      method: "get",
      path: "/pricingplans/:id/",
      status: 200,
      response: pricingPlanSchema,
    })
    .addEndpoint({
      alias: "getCharmPrice",
      method: "get",
      path: "/charm",
      status: 200,
      parameters: [
        {
          name: "price",
          type: "Query",
          schema: z.number().or(z.string()),
        },
      ],
      response: z.object({ price: z.number() }),
    })
    .addEndpoint({
      alias: "updatePricingPlan",
      method: "post",
      path: "/pricingplan/:id",
      status: 200,
      parameters: [
        {
          name: "body",
          type: "Body",
          schema: updatePricingPlanPayloadSchema,
        },
      ],
      response: z.number(),
    })
    .addEndpoint({
      alias: "addProductsToPricingPlan",
      method: "post",
      path: "/pricingplan/:id/products/add",
      status: 204,
      parameters: [
        {
          name: "body",
          type: "Body",
          schema: z.array(z.number()),
        },
      ],
      response: z.void(),
    })
    .addEndpoint({
      alias: "removeProductsFromPricingPlan",
      method: "post",
      path: "/pricingplan/products/remove",
      status: 204,
      parameters: [
        {
          name: "body",
          type: "Body",
          schema: z.array(z.number()),
        },
      ],
      response: z.void(),
    })
    .addEndpoint({
      alias: "getPricingConfiguration",
      method: "get",
      path: "/settings",
      status: 200,
      response: z.object({
        results: z.tuple([pricingOrgSchema]),
      }),
    })
    .addEndpoint({
      alias: "updatePricingConfiguration",
      method: "put",
      path: "/settings/:porgId/",
      status: 200,
      parameters: [
        {
          type: "Body",
          name: "body",
          schema: updatePricingConfigurationPayloadSchema,
        },
      ],
      response: pricingOrgSchema,
    })
    .addEndpoint({
      alias: "previewPlanCreation",
      method: "post",
      path: "/pricingplan/preview/",
      status: 200,
      parameters: [
        {
          type: "Body",
          name: "body",
          schema: z.object({
            product_list: z.array(z.number()),
            min_price: z.number(),
            max_price: z.number(),
            is_multiple_plans: z.boolean(),
          }),
        },
      ],
      response: z
        .object({
          plan_name: z.string(),
          min_price: z.number(),
          max_price: z.number(),
          current_price: z.number().nullable(),
          product_list: z.array(z.number()),
        })
        .array(),
    })
    .addEndpoint({
      alias: "bulkUpdatePricingPlanStatus",
      method: "post",
      path: "/bulk_update_status/",
      status: 204,
      parameters: [
        {
          type: "Body",
          name: "body",
          schema: z.object({
            enable: z.array(z.number()).optional(),
            disable: z.array(z.number()).optional(),
          }),
        },
      ],
      response: z.void(),
    })
    .build(),
);

const parentAsinConfigResponseSchema = z.object({
  parent: z.object({
    asin: z.string(),
    title: z.string(),
    image: z.string(),
  }),
  variationOptions: z.record(z.string(), z.array(z.object({ id: z.string(), title: z.string() }))),
  products: z.array(gvProductSchema),
});

export const experimentalApiDefinitions = prefixApi(
  "/pricing/experimental",
  apiBuilder({
    alias: "getParentAsinPricingConfig",
    method: "get",
    path: "/parent_asin/:asin",
    status: 200,
    response: parentAsinConfigResponseSchema,
    parameters: [{ type: "Query", name: "marketplace", schema: z.string() }],
  }).build(),
);
